import React, { useState, useEffect } from 'react';
import { 
  ArrowUp, 
  ArrowDown, 
  Calendar, 
  DollarSign, 
  Users, 
  ShoppingCart, 
  TrendingUp, 
  ChevronDown, 
  RefreshCw, 
  MapPin, 
  LineChart, 
  BarChart2,
  Award,
  Star,
  TrendingDown,
  Zap
} from 'lucide-react';
import { getSalesComparisonData, getManagersData, getRegionsData } from '../../data/mockData';

const Sales = ({ period, region, setRegionFilter, setPeriodFilter }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [comparison, setComparison] = useState('previous');
  const [salesData, setSalesData] = useState(null);
  const [regionalManagers, setRegionalManagers] = useState([]);
  const [districtManagers, setDistrictManagers] = useState([]);
  const [storeManagers, setStoreManagers] = useState([]);
  const [regions, setRegions] = useState([]);

  // Possible period options
  const periodOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  // Possible comparison options
  const comparisonOptions = [
    { value: 'previous', label: 'vs Previous Period' },
    { value: 'yoy', label: 'vs Same Period Last Year' },
    { value: 'budget', label: 'vs Budget' }
  ];

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      // Fetch sales comparison data
      const data = await getSalesComparisonData(period, comparison, region);
      setSalesData(data);
      
      // Fetch manager data
      const managersData = await getManagersData();
      setRegionalManagers(managersData.regionalManagers);
      setDistrictManagers(managersData.districtManagers);
      setStoreManagers(managersData.storeManagers);
      
      // Fetch regions for dropdown
      const regionsData = await getRegionsData();
      setRegions(regionsData);
      
      setIsLoading(false);
    };
    
    loadData();
  }, [period, comparison, region]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format number with commas
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en').format(value);
  };

  // Helper to get arrow icon and color based on trend
  const getTrendIndicator = (value) => {
    if (value > 0) {
      return { icon: ArrowUp, color: 'text-emerald-600', bg: 'bg-emerald-100' };
    } else if (value < 0) {
      return { icon: ArrowDown, color: 'text-rose-600', bg: 'bg-rose-100' };
    }
    return { icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-100' };
  };

  return (
    <div className="space-y-6">
      {/* Filters & Controls */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            {/* Period Filter */}
            <div className="relative">
              <select
                value={period}
                onChange={(e) => setPeriodFilter(e.target.value)}
                className="appearance-none pl-10 pr-10 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                {periodOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Comparison Filter */}
            <div className="relative">
              <select
                value={comparison}
                onChange={(e) => setComparison(e.target.value)}
                className="appearance-none pl-10 pr-10 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                {comparisonOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <LineChart className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Region Filter */}
            <div className="relative">
              <select
                value={region}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="appearance-none pl-10 pr-10 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                <option value="all">All Regions</option>
                {regions.map(region => (
                  <option key={region.id} value={region.id}>
                    {region.name} Region
                  </option>
                ))}
              </select>
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center p-12">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="ml-2 text-gray-600">Loading sales data...</span>
        </div>
      )}

      {/* Sales Metrics */}
      {!isLoading && salesData && (
        <div className="space-y-6">
          {/* Main Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Total Sales */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  salesData.changes.totalSales >= 0 
                    ? 'bg-emerald-100 text-emerald-600' 
                    : 'bg-rose-100 text-rose-600'
                }`}>
                  <div className="flex items-center">
                    {salesData.changes.totalSales >= 0 ? (
                      <ArrowUp className="h-3.5 w-3.5 mr-1" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 mr-1" />
                    )}
                    {Math.abs(salesData.changes.totalSales)}%
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500">Total Sales</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(salesData.currentPeriod.totalSales)}
              </p>
              <div className="mt-1 text-xs text-gray-500">
                {comparison === 'previous' ? 'Previous:' : 
                 comparison === 'yoy' ? 'Last Year:' : 'Budget:'}
                {' '}
                {formatCurrency(salesData.comparisonPeriod.totalSales)}
              </div>
            </div>

            {/* Average Daily Sales */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <BarChart2 className="h-6 w-6 text-emerald-600" />
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  salesData.changes.avgDailySales >= 0 
                    ? 'bg-emerald-100 text-emerald-600' 
                    : 'bg-rose-100 text-rose-600'
                }`}>
                  <div className="flex items-center">
                    {salesData.changes.avgDailySales >= 0 ? (
                      <ArrowUp className="h-3.5 w-3.5 mr-1" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 mr-1" />
                    )}
                    {Math.abs(salesData.changes.avgDailySales)}%
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500">Avg Daily Sales</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(salesData.currentPeriod.avgDailySales)}
              </p>
              <div className="mt-1 text-xs text-gray-500">
                {comparison === 'previous' ? 'Previous:' : 
                 comparison === 'yoy' ? 'Last Year:' : 'Budget:'}
                {' '}
                {formatCurrency(salesData.comparisonPeriod.avgDailySales)}
              </div>
            </div>

            {/* Transactions */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-amber-600" />
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  salesData.changes.transactions >= 0 
                    ? 'bg-emerald-100 text-emerald-600' 
                    : 'bg-rose-100 text-rose-600'
                }`}>
                  <div className="flex items-center">
                    {salesData.changes.transactions >= 0 ? (
                      <ArrowUp className="h-3.5 w-3.5 mr-1" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 mr-1" />
                    )}
                    {Math.abs(salesData.changes.transactions)}%
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500">Transactions</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(salesData.currentPeriod.transactions)}
              </p>
              <div className="mt-1 text-xs text-gray-500">
                {comparison === 'previous' ? 'Previous:' : 
                 comparison === 'yoy' ? 'Last Year:' : 'Budget:'}
                {' '}
                {formatNumber(salesData.comparisonPeriod.transactions)}
              </div>
            </div>

            {/* Average Transaction */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  salesData.changes.avgTransaction >= 0 
                    ? 'bg-emerald-100 text-emerald-600' 
                    : 'bg-rose-100 text-rose-600'
                }`}>
                  <div className="flex items-center">
                    {salesData.changes.avgTransaction >= 0 ? (
                      <ArrowUp className="h-3.5 w-3.5 mr-1" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 mr-1" />
                    )}
                    {Math.abs(salesData.changes.avgTransaction)}%
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500">Avg. Transaction</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(salesData.currentPeriod.avgTransaction)}
              </p>
              <div className="mt-1 text-xs text-gray-500">
                {comparison === 'previous' ? 'Previous:' : 
                 comparison === 'yoy' ? 'Last Year:' : 'Budget:'}
                {' '}
                {formatCurrency(salesData.comparisonPeriod.avgTransaction)}
              </div>
            </div>

            {/* Customers Served */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-pink-100 rounded-lg">
                  <Users className="h-6 w-6 text-pink-600" />
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  salesData.changes.customersServed >= 0 
                    ? 'bg-emerald-100 text-emerald-600' 
                    : 'bg-rose-100 text-rose-600'
                }`}>
                  <div className="flex items-center">
                    {salesData.changes.customersServed >= 0 ? (
                      <ArrowUp className="h-3.5 w-3.5 mr-1" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 mr-1" />
                    )}
                    {Math.abs(salesData.changes.customersServed)}%
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500">Customers Served</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(salesData.currentPeriod.customersServed)}
              </p>
              <div className="mt-1 text-xs text-gray-500">
                {comparison === 'previous' ? 'Previous:' : 
                 comparison === 'yoy' ? 'Last Year:' : 'Budget:'}
                {' '}
                {formatNumber(salesData.comparisonPeriod.customersServed)}
              </div>
            </div>
          </div>

          {/* Sales Chart */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Sales Trend</h2>
                <p className="text-sm text-gray-500">Daily sales comparison</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-blue-500 mr-1.5"></div>
                  <span className="text-xs text-gray-600">Current Period</span>
                </div>
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-gray-300 mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    {comparison === 'previous' ? 'Previous Period' : 
                     comparison === 'yoy' ? 'Last Year' : 'Budget'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="h-80 w-full">
              {/* This is where you would put your chart component */}
              <div className="h-full w-full bg-gray-50 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">Sales trend chart would be displayed here</p>
              </div>
            </div>
          </div>

          {/* Top Performing Managers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Regional Managers */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Award className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Top Regional Managers</h2>
                    <p className="text-sm text-gray-500">By performance improvement</p>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {regionalManagers
                  .sort((a, b) => b.improvement - a.improvement)
                  .slice(0, 5)
                  .map((manager, index) => {
                    const trend = getTrendIndicator(manager.improvement);
                    const TrendIcon = trend.icon;
                    
                    return (
                      <div 
                        key={manager.id}
                        className="flex items-center p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-600">
                          {index + 1}
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">{manager.name}</p>
                            <div className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center ${trend.bg} ${trend.color}`}>
                              <TrendIcon className="h-3 w-3 mr-1" />
                              {manager.improvement > 0 ? '+' : ''}{manager.improvement}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">{manager.region} - {manager.storeCount} stores</p>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            </div>
            
            {/* District Managers */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Star className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Top District Managers</h2>
                    <p className="text-sm text-gray-500">By performance improvement</p>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {districtManagers
                  .sort((a, b) => b.improvement - a.improvement)
                  .slice(0, 5)
                  .map((manager, index) => {
                    const trend = getTrendIndicator(manager.improvement);
                    const TrendIcon = trend.icon;
                    
                    return (
                      <div 
                        key={manager.id}
                        className="flex items-center p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center font-semibold text-purple-600">
                          {index + 1}
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">{manager.name}</p>
                            <div className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center ${trend.bg} ${trend.color}`}>
                              <TrendIcon className="h-3 w-3 mr-1" />
                              {manager.improvement > 0 ? '+' : ''}{manager.improvement}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">{manager.district}</p>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            </div>
            
            {/* Store Managers */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Zap className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Most Improved Stores</h2>
                    <p className="text-sm text-gray-500">By performance improvement</p>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {storeManagers
                  .sort((a, b) => b.improvement - a.improvement)
                  .slice(0, 5)
                  .map((manager, index) => {
                    const trend = getTrendIndicator(manager.improvement);
                    const TrendIcon = trend.icon;
                    
                    return (
                      <div 
                        key={manager.id}
                        className="flex items-center p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center font-semibold text-amber-600">
                          {index + 1}
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">{manager.name}</p>
                            <div className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center ${trend.bg} ${trend.color}`}>
                              <TrendIcon className="h-3 w-3 mr-1" />
                              {manager.improvement > 0 ? '+' : ''}{manager.improvement}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">{manager.storeName}</p>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales; 